
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface NavigationButtonsProps {
  isSubmitting: boolean;
  onSave: () => Promise<void>;
  previousRoute: string;
  nextRoute: string;
  isValid: boolean;
}

export const NavigationButtons = ({
  isSubmitting,
  onSave,
  previousRoute,
  nextRoute,
  isValid,
}: NavigationButtonsProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isValidating, setIsValidating] = useState(false);

  const handleNext = async () => {
    if (!isValid) {
      setIsValidating(false); // Changed from true to false to stop validation state
      toast({
        variant: "destructive",
        title: "Required fields missing",
        description: "Please add at least one service with name and price or click 'Skip'",
      });
      return;
    }
    
    setIsValidating(true);
    
    try {
      await onSave();
    } catch (error) {
      console.error("Error during save:", error);
    } finally {
      setIsValidating(false); // Ensure isValidating is set to false when done
    }
  };

  return (
    <div className="mt-12 flex justify-center gap-4">
      <Button
        variant="outline"
        className="flex items-center gap-2"
        onClick={() => navigate(previousRoute)}
        disabled={isSubmitting || isValidating}
      >
        <ChevronLeft className="w-4 h-4" />
        Back
      </Button>
      <Button
        variant="outline"
        className="w-32"
        onClick={() => navigate(nextRoute)}
        disabled={isSubmitting || isValidating}
      >
        Skip
      </Button>
      <Button 
        className="w-32 bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90"
        onClick={handleNext}
        disabled={isSubmitting || isValidating}
      >
        {isSubmitting ? "Saving..." : isValidating ? "Validating..." : "Next"}
      </Button>
    </div>
  );
};
